import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Inject,
  Input,
  OnInit,
  Optional,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { version } from '../../../../package.json';
import { DemoTourService } from '../../services/demo-tour.service';
import { SupademoComponent } from './supademo/supademo.component';

export type Note = {
  title: string;
  text: string;
  medias: string[];
  date: string;
  demo_tour?: 'true' | 'false';
};

@Component({
  selector: 'release-note',

  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    TranslatePipe,
    CommonModule,
    MatDialogModule,
    SupademoComponent,
  ],
  templateUrl: './release-note.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './release-note.component.scss',
})
export class ReleaseNoteComponent implements OnInit {
  @Input() notes: Note[] = [];
  @Input() version: string;

  private _demoTourService = inject(DemoTourService);

  public html = '';
  public $currentPage = signal<number>(0);
  public iframeTitle: string | null = null;

  public $iframeUrl = computed(() => {
    const url = this.notes[this.$currentPage()].medias?.[0]?.['url'];
    this.iframeTitle = this.notes[this.$currentPage()].medias?.[0]?.['title'] || null;
    return url ? url : null;
  });

  private BASE_PATH = 'assets/releases-notes';
  private versionWithoutPatchNumber = version.split('.').slice(0, 2).join('.');
  private MEDIAS_PATH = `${this.BASE_PATH}/medias/${this.versionWithoutPatchNumber}`;

  get submitText(): string {
    return this.$currentPage() === this.notes?.length - 1 ? 'COMMON.EXPLORE' : 'COMMON.NEXT';
  }

  get previousText(): string {
    return this.$currentPage() === 0 ? 'COMMON.CLOSE' : 'COMMON.PREVIOUS';
  }

  get mediaSrc(): string {
    if (!this.notes) return '';
    const media = this.notes[this.$currentPage()].medias[0];
    return media ? `${this.MEDIAS_PATH}/${media}` : '';
  }

  get isPicture(): boolean {
    const regex = /\.(png|webp)$/;
    return regex.test(this.notes[this.$currentPage()].medias?.[0]);
  }

  get isIframe(): boolean {
    return this.notes[this.$currentPage()].medias?.[0]?.['url'];
  }

  get isDemoTour(): boolean {
    return this.notes[this.$currentPage()]?.demo_tour === 'true';
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<ReleaseNoteComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public injectedNotes?: Note[]
  ) {
    if (injectedNotes) {
      this.notes = injectedNotes;
    }
  }

  ngOnInit(): void {
    if (this.version) this.MEDIAS_PATH = `${this.BASE_PATH}/medias/${this.version}`;
  }

  public onPrevious() {
    if (this.$currentPage() !== 0) this.$currentPage.set(this.$currentPage() - 1);
    else this.dialogRef.close();
  }

  public onNext() {
    if (this.$currentPage() !== this.notes?.length - 1) this.$currentPage.set(this.$currentPage() + 1);
    else this.dialogRef.close();
  }

  public runDemoTour() {
    this.dialogRef?.close();
    this._demoTourService.runDemoTour();
  }
}
